// src/screens/SignUpWizard.js
// ============================================================================
// SIGNUP WIZARD - Multi-step registration flow
// Supports email/password, Google, Discord, and Twitch OAuth signup
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  View, Text, TextInput, TouchableOpacity, StyleSheet,
  ScrollView, ActivityIndicator, KeyboardAvoidingView, Platform,
  Linking,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { doc, setDoc, getDoc, serverTimestamp, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { auth, db, functions } from '../../firebaseConfig';
import { preferRedirectForGoogle, withTimeout, googleSignInErrorText } from '../utils/googleAuth';
import Footer from '../components/Footer';
import { CURRENT_TOS_VERSION } from '../constants/tosUpdate';
import { openTermsOfService } from '../utils/openTermsOfService';
import { saveOAuthState } from '../utils/oauthState';
import { isDesktopApp } from '../utils/platform';
import {
  blocksPhoneOnlyCompletion,
  signOutWithDesktopPush,
  saveOAuthNext,
  getSessionStorage,
  PHONE_NO_ACCOUNT_TEXT,
  SIGN_OUT_FAILED_LINE,
} from '../utils/phoneNumber';

// ============================================================================
// CONSTANTS
// ============================================================================

// The Privacy Policy is the static page (web/privacy-policy.html, served at
// /privacy-policy), the same URL the Footer opens. Web: a new tab, opened
// synchronously inside the press handler so it isn't blocked as a popup and
// the wizard keeps its state. Native: hand the URL to the OS, as Footer does.
const PRIVACY_POLICY_URL = 'https://critcash.com/privacy-policy';
const openPrivacyPolicy = () => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(PRIVACY_POLICY_URL, '_blank', 'noopener');
    return;
  }
  Linking.canOpenURL(PRIVACY_POLICY_URL)
    .then((supported) => { if (supported) Linking.openURL(PRIVACY_POLICY_URL); })
    .catch(() => {});
};

const STEPS = {
  ACCOUNT: 0,
  PERSONAL: 1,
  TERMS: 2,
};

const STEP_TITLES = {
  [STEPS.ACCOUNT]: 'Create Account',
  [STEPS.PERSONAL]: 'Personal Info',
  [STEPS.TERMS]: 'Terms & Conditions',
};

// ============================================================================
// STEP INDICATOR COMPONENT
// ============================================================================

const StepIndicator = ({ currentStep, totalSteps }) => (
  <View style={styles.stepIndicator}>
    {[...Array(totalSteps)].map((_, index) => (
      <View key={index} style={styles.stepRow}>
        <View style={[
          styles.stepDot,
          index <= currentStep && styles.stepDotActive,
          index < currentStep && styles.stepDotComplete,
        ]}>
          {index < currentStep ? (
            <Ionicons name="checkmark" size={16} color="#000" />
          ) : (
            <Text style={styles.stepNumber}>{index + 1}</Text>
          )}
        </View>
        {index < totalSteps - 1 && (
          <View style={[
            styles.stepLine,
            index < currentStep && styles.stepLineActive,
          ]} />
        )}
      </View>
    ))}
  </View>
);

// ============================================================================
// INPUT COMPONENT
// ============================================================================

const FormInput = ({ 
  label, 
  value, 
  onChangeText, 
  placeholder, 
  secureTextEntry,
  keyboardType,
  autoCapitalize,
  error,
  maxLength,
}) => (
  <View style={styles.inputGroup}>
    <Text style={styles.inputLabel}>{label}</Text>
    <TextInput
      style={[styles.input, error && styles.inputError]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#666"
      secureTextEntry={secureTextEntry}
      keyboardType={keyboardType || 'default'}
      autoCapitalize={autoCapitalize || 'none'}
      maxLength={maxLength}
    />
    {error && <Text style={styles.errorText}>{error}</Text>}
  </View>
);

// ============================================================================
// STEP 1: ACCOUNT CREATION
// ============================================================================

const AccountStep = ({ 
  email, setEmail, 
  password, setPassword, 
  confirmPassword, setConfirmPassword,
  errors,
  oauthProvider,
  onGoogleSignUp,
  onDiscordSignUp,
  onTwitchSignUp,
  isLoading,
}) => (
  <View style={styles.stepContent}>
    <Text style={styles.stepDescription}>
      {oauthProvider 
        ? `You're signing up with ${oauthProvider}. Just a few more details needed.`
        : "Create your account with email and password, or sign up with a connected account."}
    </Text>

    {!oauthProvider && (
      <>
        <FormInput
          label="Email"
          value={email}
          onChangeText={setEmail}
          placeholder="your@email.com"
          keyboardType="email-address"
          error={errors.email}
        />
        
        <FormInput
          label="Password"
          value={password}
          onChangeText={setPassword}
          placeholder="Min 8 characters"
          secureTextEntry
          error={errors.password}
        />
        
        <FormInput
          label="Confirm Password"
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          placeholder="Re-enter password"
          secureTextEntry
          error={errors.confirmPassword}
        />

        <View style={styles.divider}>
          <View style={styles.dividerLine} />
          <Text style={styles.dividerText}>OR</Text>
          <View style={styles.dividerLine} />
        </View>

        {/* OAuth Buttons */}
        <View style={styles.oauthButtons}>
          <TouchableOpacity 
            style={styles.oauthButton}
            onPress={onGoogleSignUp}
            disabled={isLoading}
          >
            <View style={styles.oauthButtonContent}>
              <Ionicons name="logo-google" size={20} color="#fff" />
              <Text style={styles.oauthButtonText}>Google</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.oauthButton, styles.discordButton]}
            onPress={onDiscordSignUp}
            disabled={isLoading}
          >
            <View style={styles.oauthButtonContent}>
              <Ionicons name="logo-discord" size={20} color="#fff" />
              <Text style={styles.oauthButtonText}>Discord</Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.oauthButton, styles.twitchButton]}
            onPress={onTwitchSignUp}
            disabled={isLoading}
          >
            <View style={styles.oauthButtonContent}>
              <Ionicons name="logo-twitch" size={20} color="#fff" />
              <Text style={styles.oauthButtonText}>Twitch</Text>
            </View>
          </TouchableOpacity>
        </View>
      </>
    )}

    {oauthProvider && (
      <View style={styles.oauthConfirm}>
        <Ionicons name="checkmark-circle" size={20} color="#2ECC71" style={{ marginRight: 10 }} />
        <Text style={styles.oauthConfirmText}>Signed in as {email}</Text>
      </View>
    )}
  </View>
);

// ============================================================================
// STEP 2: PERSONAL INFO
// ============================================================================

const PersonalStep = ({
  firstName, setFirstName,
  lastName, setLastName,
  userName, setUserName,
  dateOfBirth, setDateOfBirth,
  errors,
}) => (
  <View style={styles.stepContent}>
    <Text style={styles.stepDescription}>
      Tell us a bit about yourself. Your username will be visible to other players.
    </Text>

    <View style={styles.nameRow}>
      <View style={styles.nameField}>
        <FormInput
          label="First Name"
          value={firstName}
          onChangeText={setFirstName}
          placeholder="John"
          autoCapitalize="words"
          error={errors.firstName}
        />
      </View>
      <View style={styles.nameField}>
        <FormInput
          label="Last Name"
          value={lastName}
          onChangeText={setLastName}
          placeholder="Doe"
          autoCapitalize="words"
          error={errors.lastName}
        />
      </View>
    </View>

    <FormInput
      label="Username"
      value={userName}
      onChangeText={(text) => setUserName(text.replace(/[^A-Za-z0-9_]/g, ''))}
      placeholder="your_gamertag"
      maxLength={20}
      error={errors.userName}
    />
    <Text style={styles.inputHint}>
      Letters, numbers, and underscores only. This is public.
    </Text>

    <FormInput
      label="Date of Birth"
      value={dateOfBirth}
      onChangeText={(text) => {
        // Auto-format as MM/DD/YYYY
        const cleaned = text.replace(/\D/g, '');
        let formatted = '';
        if (cleaned.length > 0) formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) formatted += '/' + cleaned.substring(2, 4);
        if (cleaned.length > 4) formatted += '/' + cleaned.substring(4, 8);
        setDateOfBirth(formatted);
      }}
      placeholder="MM/DD/YYYY"
      keyboardType="numeric"
      maxLength={10}
      error={errors.dateOfBirth}
    />
    <Text style={styles.inputHint}>
      You must be 18 or older to participate on CritCash.
    </Text>
  </View>
);

// ============================================================================
// STEP 3: TERMS & CONDITIONS
// ============================================================================

const TermsStep = ({ 
  agreeTerms, setAgreeTerms,
  agreePrivacy, setAgreePrivacy,
  agreeAge, setAgreeAge,
  errors,
  navigation,
}) => (
  <View style={styles.stepContent}>
    <Text style={styles.stepDescription}>
      Please review and accept our terms to complete your registration.
    </Text>

    <TouchableOpacity 
      style={styles.checkboxRow}
      onPress={() => setAgreeTerms(!agreeTerms)}
    >
      <View style={[styles.checkbox, agreeTerms && styles.checkboxChecked]}>
        {agreeTerms && <Ionicons name="checkmark" size={14} color="#000" />}
      </View>
      <Text style={styles.checkboxLabel}>
        I agree to the{' '}
        <Text 
          style={styles.link}
          onPress={() => openTermsOfService(navigation)}
        >
          Terms of Service
        </Text>
      </Text>
    </TouchableOpacity>

    <TouchableOpacity 
      style={styles.checkboxRow}
      onPress={() => setAgreePrivacy(!agreePrivacy)}
    >
      <View style={[styles.checkbox, agreePrivacy && styles.checkboxChecked]}>
        {agreePrivacy && <Ionicons name="checkmark" size={14} color="#000" />}
      </View>
      <Text style={styles.checkboxLabel}>
        I agree to the{' '}
        <Text 
          style={styles.link}
          onPress={openPrivacyPolicy}
        >
          Privacy Policy
        </Text>
      </Text>
    </TouchableOpacity>

    <TouchableOpacity 
      style={styles.checkboxRow}
      onPress={() => setAgreeAge(!agreeAge)}
    >
      <View style={[styles.checkbox, agreeAge && styles.checkboxChecked]}>
        {agreeAge && <Ionicons name="checkmark" size={14} color="#000" />}
      </View>
      <Text style={styles.checkboxLabel}>
        I confirm that I am at least 18 years old
      </Text>
    </TouchableOpacity>

    {errors.terms && (
      <Text style={styles.errorText}>{errors.terms}</Text>
    )}

    <View style={styles.termsNote}>
      <Text style={styles.termsNoteText}>
        By creating an account, you acknowledge that CritCash is a gameplay
        content platform and that how results are confirmed depends on the game. Please play responsibly.
      </Text>
    </View>
  </View>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SignUpWizard({ navigation, route }) {
  // Pre-filled data from Google OAuth
  const prefill = route?.params || {};

  // OAuth-completion mode: the user has ALREADY authenticated via a Discord/
  // Twitch redirect or Google (their account is pending, or its users doc has
  // not been written yet), and we're here only to collect DOB + username + ToS
  // and finalize server-side via completeOAuthSignup — which also bootstraps a
  // missing users doc from the Auth record. The account step is skipped
  // entirely. Set by LoginScreen / ProfileSettings via the route param, or
  // flipped in place by the in-wizard Google button (continueGoogleSignUp).
  const [oauthCompletion, setOauthCompletion] = useState(!!prefill.oauthCompletion);
  const prefillNameParts = (prefill.displayName || '').trim().split(/\s+/);

  // Current step — OAuth-completion starts at Personal (no account step).
  const [currentStep, setCurrentStep] = useState(oauthCompletion ? STEPS.PERSONAL : STEPS.ACCOUNT);

  // Form state
  const [email, setEmail] = useState(prefill.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState(prefill.firstName || prefillNameParts[0] || '');
  const [lastName, setLastName] = useState(prefill.lastName || prefillNameParts.slice(1).join(' ') || '');
  const [userName, setUserName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeAge, setAgreeAge] = useState(false);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // OAuth state - tracks which provider was used (null, 'Google', 'Discord', 'Twitch')
  const [oauthProvider, setOauthProvider] = useState(prefill.provider || (prefill.email ? 'Google' : null));
  // Guard: don't finalize with an unverified underage/duplicate account. All
  // three are re-checked server-side in completeOAuthSignup regardless.
  const [oauthUser, setOauthUser] = useState(null);

  // Phone sign-in never creates an account (DESKTOP_APP_DESIGN_2026-10-20.md
  // S2.4, P3). A phone-only record (a ghost whose client delete failed) that
  // reaches completion mode, through /signup?oauthCompletion=1, ?email= or
  // ProfileSettings, sees the pinned message and Log out, and the wizard
  // never calls completeOAuthSignup for it. The auth listener re-checks when
  // the signed-in record changes; route-param changes re-render the check;
  // handleCreateAccount checks auth.currentUser again at the press.
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const [phoneLoggingOut, setPhoneLoggingOut] = useState(false);
  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u)), []);
  const completionMode = oauthCompletion || !!oauthProvider;
  const phoneBlocked = phoneLoggingOut || blocksPhoneOnlyCompletion({ completionMode, user: authUser });

  // navigate('SignUp', {oauthCompletion:true}) onto an ALREADY-mounted wizard
  // (stack v6 pops back to the existing instance and only swaps its params —
  // the useState initializers above never re-run) must still enter completion
  // mode. The finish-onboarding guard and ProfileSettings both navigate here.
  useEffect(() => {
    if (!route?.params?.oauthCompletion || oauthCompletion) return;
    setOauthCompletion(true);
    if (route.params.provider) setOauthProvider(route.params.provider);
    if (route.params.email) setEmail(route.params.email);
    const parts = (route.params.displayName || '').trim().split(/\s+/);
    if (parts[0]) setFirstName((v) => v || parts[0]);
    if (parts.length > 1) setLastName((v) => v || parts.slice(1).join(' '));
    setErrors({});
    setCurrentStep(STEPS.PERSONAL);
  }, [route?.params?.oauthCompletion]);

  // Auto-advance if coming from OAuth callback
  useEffect(() => {
    if (prefill.email && currentStep === STEPS.ACCOUNT) {
      // Small delay to show the confirmation, then advance
      setTimeout(() => setCurrentStep(STEPS.PERSONAL), 500);
    }
  }, []);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateStep = (step) => {
    const newErrors = {};

    if (step === STEPS.ACCOUNT && !oauthProvider) {
      if (!email) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Invalid email format';
      
      if (!password) newErrors.password = 'Password is required';
      else if (password.length < 8) newErrors.password = 'Password must be at least 8 characters';
      
      if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }

    if (step === STEPS.PERSONAL) {
      if (!firstName.trim()) newErrors.firstName = 'Required';
      if (!lastName.trim()) newErrors.lastName = 'Required';
      
      if (!userName.trim()) newErrors.userName = 'Username is required';
      else if (userName.trim().length < 3) newErrors.userName = 'At least 3 characters';
      else if (userName.trim().length > 20) newErrors.userName = 'Max 20 characters';
      else if (!/^[A-Za-z0-9_]+$/.test(userName.trim())) newErrors.userName = 'Letters, numbers, and _ only';
      
      if (!dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      else {
        const parts = dateOfBirth.split('/');
        if (parts.length !== 3 || parts[2]?.length !== 4) {
          newErrors.dateOfBirth = 'Use format MM/DD/YYYY';
        } else {
          const dob = new Date(parts[2], parts[0] - 1, parts[1]);
          const age = Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
          if (age < 18) newErrors.dateOfBirth = 'You must be 18 or older';
          if (age > 120) newErrors.dateOfBirth = 'Invalid date';
        }
      }
    }

    if (step === STEPS.TERMS) {
      if (!agreeTerms || !agreePrivacy || !agreeAge) {
        newErrors.terms = 'You must accept all agreements to continue';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // OAUTH HANDLERS
  // ============================================================================

  // Shared post-auth routing for both the popup and redirect Google flows.
  // The users-doc read is capped so a stalled connection surfaces a retryable
  // error instead of an endless spinner.
  const continueGoogleSignUp = async (user) => {
    const userDoc = await withTimeout(getDoc(doc(db, 'users', user.uid)), 10000, 'Sign-in');
    if (userDoc.exists() && userDoc.data()?.onboardingComplete !== false) {
      // Fully onboarded account — straight to the lobby.
      navigation.replace('Home');
      return;
    }

    // New (or still-pending) Google account. The users doc is written
    // server-side — by the onAuthUserCreated trigger, or bootstrapped inside
    // completeOAuthSignup — so the wizard only collects DOB / username / ToS
    // and finalizes through the same callable Discord/Twitch use. The browser
    // never writes the account doc itself anymore; that was the stranding gap.
    setOauthUser(user);
    setOauthProvider('Google');
    setOauthCompletion(true);
    setEmail(user.email || '');

    const nameParts = (user.displayName || '').split(' ');
    setFirstName(nameParts[0] || '');
    setLastName(nameParts.slice(1).join(' ') || '');

    setCurrentStep(STEPS.PERSONAL);
  };

  const handleGoogleSignUp = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      const provider = new GoogleAuthProvider();

      // Mobile web: the popup flow can complete on Google's side yet never
      // deliver the result back to the page (in-app browsers sever the opener
      // channel), leaving the user buffering forever. Use the full-page
      // redirect instead; the mount effect below picks up the return leg.
      // The CritCast desktop app takes the redirect too (S2.6).
      if (preferRedirectForGoogle()) {
        await signInWithRedirect(auth, provider);
        return; // page navigates away; keep the button disabled until it does
      }

      const result = await signInWithPopup(auth, provider);
      await continueGoogleSignUp(result.user);
      setIsLoading(false);
    } catch (error) {
      showGoogleError(error);
      setIsLoading(false);
    }
  };

  // One catch for both Google legs (popup and redirect). Logs the code only:
  // an AuthError's customData can carry OAuth tokens, and the member sees
  // member copy, never the SDK message (S2.4). A closed popup shows nothing.
  // A disabled (deleted, restorable) account goes to the self-serve restore
  // screen, as LoginScreen's handleGoogleError does.
  const showGoogleError = (error) => {
    if (error && error.code === 'auth/user-disabled') {
      navigation.replace('RestoreAccount');
      return;
    }
    const line = googleSignInErrorText(error);
    if (!line) return;
    console.error('Google sign up error:', error && error.code);
    setErrors({ email: line });
  };

  // Return leg of the mobile signInWithRedirect flow. On a normal visit
  // getRedirectResult resolves null immediately and this is a no-op.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          setIsLoading(true);
          await continueGoogleSignUp(result.user);
        }
      } catch (error) {
        showGoogleError(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Full-page redirect to the provider. Returns to /auth/<provider>/callback,
  // which (via LoginScreen) exchanges the code and forwards new/pending users
  // back into this wizard in completion mode.
  const startSocialSignUp = async (provider, authUrlFn) => {
    setIsLoading(true);
    setErrors({});
    try {
      const result = await httpsCallable(getFunctions(), authUrlFn)();
      if (result.data?.authUrl) {
        if (Platform.OS === 'web') {
          // R0b: LoginScreen checks this one-time state when the provider
          // sends the browser back (login CSRF guard).
          sessionStorage.setItem('oauth_provider', provider);
          saveOAuthNext(getSessionStorage(), null); // no next from sign-up: clear a stale one
          saveOAuthState(result.data.state);
          window.location.href = result.data.authUrl;
        } else {
          await Linking.openURL(result.data.authUrl);
        }
      }
    } catch (error) {
      console.error(`${provider} sign up error:`, error);
      setErrors({ email: error.message || `Failed to connect to ${provider}` });
      setIsLoading(false);
    }
  };

  const handleDiscordSignUp = () => startSocialSignUp('discord', 'getDiscordAuthUrl');
  const handleTwitchSignUp = () => startSocialSignUp('twitch', 'getTwitchAuthUrl');

  // ============================================================================
  // NAVIGATION HANDLERS
  // ============================================================================

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    
    if (currentStep < STEPS.TERMS) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step - create account
      await handleCreateAccount();
    }
  };

  const handleBack = () => {
    // OAuth completion has no account step — going back from Personal exits.
    if (oauthCompletion && currentStep === STEPS.PERSONAL) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigation.navigate('Login');
    } else if (currentStep > STEPS.ACCOUNT) {
      setCurrentStep(currentStep - 1);
    } else if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Login');
    }
  };

  const handleCreateAccount = async () => {
    // P3: never finish a phone-only record (the listener may not have run yet).
    if (blocksPhoneOnlyCompletion({ completionMode: oauthCompletion || !!oauthProvider, user: auth.currentUser })) {
      setAuthUser(auth.currentUser);
      setPhoneLoggingOut(false);
      setErrors({});
      return;
    }
    setIsLoading(true);
    setErrors({});

    // OAuth-completion path (Discord / Twitch / Google): the user is already
    // authenticated. Finalize server-side — completeOAuthSignup bootstraps the
    // users doc if it's missing, enforces the 18+ age gate, username
    // uniqueness, and ToS acceptance, records referral attribution, then
    // grants Crits. Any signed-in OAuth account takes this path; the
    // client-side doc write below is for email/password only.
    if (oauthCompletion || oauthProvider) {
      try {
        const completeFn = httpsCallable(getFunctions(), 'completeOAuthSignup');
        const inviteCode = route?.params?.inviteCode || null;
        await completeFn({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth,
          userName: userName.trim(),
          referralCode: route?.params?.ref || null,
          inviteCode,
          // The Terms version this wizard showed; the server stamps it.
          tosVersion: CURRENT_TOS_VERSION,
        });
        // Invite-code redemption mirrors the email path (best-effort; the
        // account is already finalized, so a failure here can't block them).
        if (inviteCode) {
          try {
            await httpsCallable(getFunctions(), 'redeemInviteCode')({ code: inviteCode });
          } catch (redeemErr) {
            console.error('Failed to redeem invite code:', redeemErr);
          }
        }
        navigation.replace('Home');
      } catch (error) {
        console.error('OAuth signup completion error:', error);
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('username') || (error?.code || '').includes('already-exists')) {
          setErrors({ userName: 'Username is already taken' });
          setCurrentStep(STEPS.PERSONAL);
        } else if (msg.includes('18') || msg.includes('date of birth')) {
          setErrors({ dateOfBirth: 'You must be 18 or older, with a valid date of birth' });
          setCurrentStep(STEPS.PERSONAL);
        } else {
          setErrors({ terms: error?.message || 'Could not complete signup. Please try again.' });
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      // Username: keep the case the user typed (shown everywhere as-is);
      // userNameLower is the case-insensitive uniqueness + search key.
      const handle = userName.trim();
      const handleLower = handle.toLowerCase();

      // Uniqueness check BEFORE creating the auth account, so a name
      // collision doesn't leave an orphaned Firebase Auth user behind.
      const taken = await getDocs(query(
        collection(db, 'public_profiles'),
        where('userNameLower', '==', handleLower),
        limit(1)
      ));
      if (!taken.empty) {
        setErrors({ userName: 'Username is already taken' });
        setCurrentStep(STEPS.PERSONAL);
        setIsLoading(false);
        return;
      }
      // For OAuth users, use oauthUser state or fall back to auth.currentUser
      // This handles cases where user arrived via route params prefill
      let user = oauthUser || auth.currentUser;
      
      // Create Firebase Auth account if not OAuth user
      if (!oauthProvider) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        user = result.user;
        
        // Update display name
        await updateProfile(user, {
          displayName: `${firstName} ${lastName}`.trim(),
        });
      }
      
      // Safety check: ensure we have an authenticated user
      if (!user) {
        throw new Error(`No authenticated user found. Please try signing in with ${oauthProvider || 'your credentials'} again.`);
      }
      
      // Parse DOB
      const dobParts = dateOfBirth.split('/');
      const dob = new Date(dobParts[2], dobParts[0] - 1, dobParts[1]);
      
      // Create user document
      const inviteCode = route?.params?.inviteCode;
      // Referral link (?ref=CODE on /signup) — stamped onto the user doc and
      // resolved server-side by the sendWelcomeEmail trigger (two-sided Crit
      // reward + the referrer's referrals comp signal). UPDATE-blocked by rules.
      const referralCode = route?.params?.ref || null;
      const userData = {
        email: user.email || email,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        userName: handle,
        dateOfBirth: dob.toISOString(),
        balanceCents: 0,
        role: 'user',
        isHost: false,
        authProvider: oauthProvider ? oauthProvider.toLowerCase() : 'email',
        betaAccess: true, // signups are open (public launch 2026-07-15)
        inviteCode: inviteCode || null,
        pendingReferralCode: referralCode,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        verification: {
          identity: { status: 'unverified' },
          phone: { status: 'unverified' },
          tos: {
            status: 'verified',
            acceptedAt: serverTimestamp(),
            version: CURRENT_TOS_VERSION,
          },
        },
        stats: {
          wins: 0,
          losses: 0,
          totalEarnings: 0,
        },
      };
      
      await setDoc(doc(db, 'users', user.uid), userData);
      
      // Create public profile. userName keeps the user's typed case (founder
      // 2026-07-14); userNameLower powers the user-search prefix query and
      // case-insensitive lookups — always write it alongside userName,
      // matching what updateProfile does.
      const publicProfile = {
        userName: handle,
        userNameLower: handleLower,
        profilePhotoUrl: user.photoURL || null,
        isHost: false,
        createdAt: serverTimestamp(),
      };
      
      await setDoc(doc(db, 'public_profiles', user.uid), publicProfile);
      
      // Redeem invite code (increments redemption count on the code)
      if (inviteCode) {
        try {
          const redeemFn = httpsCallable(getFunctions(), 'redeemInviteCode');
          await redeemFn({ code: inviteCode });
        } catch (redeemErr) {
          console.error('Failed to redeem invite code:', redeemErr);
          // betaAccess is already set on the doc, so user won't be blocked
        }
      }
      
      // Success! Navigate to lobby.
      // The onAuthStateChanged listener in App.js will also detect the new user
      // and redirect to Lobby if we're still on the SignUp screen, so this is
      // a belt-and-suspenders approach.
      navigation.replace('Home');
      
    } catch (error) {
      console.error('Account creation error:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ email: 'This email is already registered. Sign in, or reset your password from the sign-in page.' });
        setCurrentStep(STEPS.ACCOUNT);
      } else if (error.code === 'auth/weak-password') {
        setErrors({ password: 'Password is too weak. Use at least 8 characters.' });
        setCurrentStep(STEPS.ACCOUNT);
      } else {
        setErrors({ terms: error.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.ACCOUNT:
        return (
          <AccountStep
            email={email}
            setEmail={setEmail}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            errors={errors}
            oauthProvider={oauthProvider}
            onGoogleSignUp={handleGoogleSignUp}
            onDiscordSignUp={handleDiscordSignUp}
            onTwitchSignUp={handleTwitchSignUp}
            isLoading={isLoading}
          />
        );
      case STEPS.PERSONAL:
        return (
          <PersonalStep
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            userName={userName}
            setUserName={setUserName}
            dateOfBirth={dateOfBirth}
            setDateOfBirth={setDateOfBirth}
            errors={errors}
          />
        );
      case STEPS.TERMS:
        return (
          <TermsStep
            agreeTerms={agreeTerms}
            setAgreeTerms={setAgreeTerms}
            agreePrivacy={agreePrivacy}
            setAgreePrivacy={setAgreePrivacy}
            agreeAge={agreeAge}
            setAgreeAge={setAgreeAge}
            errors={errors}
            navigation={navigation}
          />
        );
      default:
        return null;
    }
  };

  // Log out from the phone-only block: a member's button, so inside the
  // desktop app it tells the app first (S2.2 item 4), then signs out and
  // opens Login. The block stays up until the screen changes.
  const handlePhoneOnlyLogOut = () => {
    setPhoneLoggingOut(true);
    setIsLoading(true);
    setErrors({});
    signOutWithDesktopPush({ signOut, auth, isDesktop: isDesktopApp() })
      .then(() => navigation.replace('Login'))
      .catch(() => {
        setPhoneLoggingOut(false);
        setErrors({ phoneOnly: SIGN_OUT_FAILED_LINE });
      })
      .finally(() => setIsLoading(false));
  };

  if (phoneBlocked) {
    return (
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          <View style={styles.phoneOnlyCard}>
            <Ionicons name="call-outline" size={40} color="#FCD000" />
            <Text style={styles.phoneOnlyText}>{PHONE_NO_ACCOUNT_TEXT}</Text>
            {errors.phoneOnly ? <Text style={styles.errorText}>{errors.phoneOnly}</Text> : null}
            <TouchableOpacity
              style={[styles.phoneOnlyButton, isLoading && styles.buttonDisabled]}
              onPress={handlePhoneOnlyLogOut}
              disabled={isLoading}
            >
              {isLoading ? (
                <ActivityIndicator color="#000" />
              ) : (
                <Text style={styles.nextButtonText}>Log out</Text>
              )}
            </TouchableOpacity>
          </View>
          <Footer navigation={navigation} />
        </ScrollView>
        <StatusBar style="light" />
      </KeyboardAvoidingView>
    );
  }

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.logo}>CritCash</Text>
          <Text style={styles.title}>{STEP_TITLES[currentStep]}</Text>
        </View>

        {/* Step Indicator — OAuth completion has no account step, so it shows
            two dots (Personal, Terms) offset by one. */}
        <StepIndicator
          currentStep={oauthCompletion ? currentStep - 1 : currentStep}
          totalSteps={oauthCompletion ? 2 : 3}
        />

        {/* Step Content */}
        {renderStep()}

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={handleBack}
            disabled={isLoading}
          >
            <View style={styles.buttonContent}>
              <Ionicons name="arrow-back" size={16} color="#888" style={{ marginRight: 4 }} />
              <Text style={styles.backButtonText}>
                {currentStep === STEPS.ACCOUNT ? 'Back' : 'Previous'}
              </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity 
            style={[styles.nextButton, isLoading && styles.buttonDisabled]}
            onPress={handleNext}
            disabled={isLoading}
          >
            {isLoading ? (
              <ActivityIndicator color="#000" />
            ) : (
              <View style={styles.buttonContent}>
                <Text style={styles.nextButtonText}>
                  {currentStep === STEPS.TERMS ? 'Create Account' : 'Continue'}
                </Text>
                {currentStep !== STEPS.TERMS && (
                  <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 4 }} />
                )}
              </View>
            )}
          </TouchableOpacity>
        </View>

        {/* Login Link */}
        <TouchableOpacity 
          style={styles.loginLink}
          onPress={() => navigation.navigate('Login')}
        >
          <Text style={styles.loginLinkText}>
            Already have an account? <Text style={styles.loginLinkBold}>Log in</Text>
          </Text>
        </TouchableOpacity>

        <Footer navigation={navigation} />
      </ScrollView>
      
      <StatusBar style="light" />
    </KeyboardAvoidingView>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  scrollContent: {
    flexGrow: 1,
    padding: 20,
  },

  // Phone-only block (S2.4, P3)
  phoneOnlyCard: {
    alignItems: 'center',
    alignSelf: 'center',
    width: '100%',
    maxWidth: 420,
    marginTop: 60,
    marginBottom: 32,
    padding: 24,
    gap: 16,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    borderRadius: 12,
    backgroundColor: '#0d0d0d',
  },
  phoneOnlyText: {
    color: '#fff',
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
  },
  phoneOnlyButton: {
    alignSelf: 'stretch',
    backgroundColor: '#FCD000',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  
  // Header
  header: {
    alignItems: 'center',
    marginBottom: 24,
    marginTop: 20,
  },
  logo: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 8,
  },
  title: {
    fontSize: 22,
    fontWeight: '600',
    color: '#fff',
  },

  // Step Indicator
  stepIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepDot: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#222',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#333',
  },
  stepDotActive: {
    borderColor: '#FCD000',
    backgroundColor: '#1a1500',
  },
  stepDotComplete: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  stepNumber: {
    color: '#666',
    fontSize: 14,
    fontWeight: '600',
  },
  stepCheck: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  stepLine: {
    width: 40,
    height: 2,
    backgroundColor: '#333',
    marginHorizontal: 8,
  },
  stepLineActive: {
    backgroundColor: '#FCD000',
  },

  // Step Content
  stepContent: {
    marginBottom: 24,
  },
  stepDescription: {
    color: '#888',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },

  // Inputs
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    color: '#aaa',
    fontSize: 13,
    marginBottom: 6,
    fontWeight: '500',
  },
  input: {
    backgroundColor: '#111',
    borderWidth: 1,
    borderColor: '#333',
    borderRadius: 8,
    padding: 14,
    color: '#fff',
    fontSize: 16,
  },
  inputError: {
    borderColor: '#ff4d4d',
  },
  inputHint: {
    color: '#555',
    fontSize: 12,
    marginTop: 4,
  },
  errorText: {
    color: '#ff4d4d',
    fontSize: 12,
    marginTop: 4,
  },

  // Name Row
  nameRow: {
    flexDirection: 'row',
    gap: 12,
  },
  nameField: {
    flex: 1,
  },

  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#333',
  },
  dividerText: {
    color: '#666',
    paddingHorizontal: 16,
    fontSize: 12,
  },

  // OAuth Buttons
  oauthButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  oauthButton: {
    flex: 1,
    backgroundColor: '#333',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
  },
  oauthButtonContent: {
    alignItems: 'center',
    gap: 6,
  },
  oauthButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  discordButton: {
    backgroundColor: '#5865F2',
  },
  twitchButton: {
    backgroundColor: '#9146FF',
  },
  oauthConfirm: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0a1a0a',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#2ECC7150',
  },
  oauthConfirmText: {
    color: '#2ECC71',
    fontSize: 14,
  },

  // Legacy Google styles (kept for compatibility)
  googleButton: {
    backgroundColor: '#222',
    borderWidth: 1,
    borderColor: '#444',
    borderRadius: 8,
    padding: 14,
    alignItems: 'center',
  },
  googleButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
  googleButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  googleConfirm: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0a1a0a',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#2ECC7150',
  },
  googleConfirmIcon: {
    color: '#2ECC71',
    fontSize: 20,
    marginRight: 10,
  },
  googleConfirmText: {
    color: '#2ECC71',
    fontSize: 14,
  },

  // Checkboxes
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#444',
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: '#FCD000',
    borderColor: '#FCD000',
  },
  checkmark: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    color: '#ccc',
    fontSize: 14,
    flex: 1,
    lineHeight: 22,
  },
  link: {
    color: '#FCD000',
    textDecorationLine: 'underline',
  },
  termsNote: {
    backgroundColor: '#111',
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#222',
  },
  termsNoteText: {
    color: '#666',
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
  },

  // Buttons
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  backButton: {
    flex: 1,
    backgroundColor: '#222',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  backButtonText: {
    color: '#888',
    fontSize: 16,
    fontWeight: '500',
  },
  nextButton: {
    flex: 2,
    backgroundColor: '#FCD000',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  nextButtonText: {
    color: '#000',
    fontSize: 16,
    fontWeight: 'bold',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  googleButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Login Link
  loginLink: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  loginLinkText: {
    color: '#666',
    fontSize: 14,
  },
  loginLinkBold: {
    color: '#FCD000',
    fontWeight: '600',
  },
});